import { Button, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Expo48Form, { type Expo48FormData } from "@/components/expo-48-form";
import QRCode from "@/components/qr-code";
import { useCreateRegistration } from "@/lib/queries/use-registration";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [successUuid, setSuccessUuid] = useState<string | null>(null);
  const [successName, setSuccessName] = useState<string | null>(null);

  const createRegistration = useCreateRegistration();

  useEffect(() => {
    document.title = "Register | House & Condo Expo";
  }, []);

  const onSubmit = async (data: Expo48FormData) => {
    try {
      const result = await createRegistration.mutateAsync({
        name: data.name,
        surname: data.surname,
        phone: data.phone,
        email: data.email,
        age: data.age && data.age.trim() ? Number(data.age) : undefined,
        gender: data.gender,
        expoVersion: "48",
        expoData: {
          interests: data.interests,
          interestOther: data.interestOther,
          banks: data.banks,
          bankOther: data.bankOther,
          propertyCharacteristics: data.propertyCharacteristics,
          priceRange: data.priceRange,
          locationData: data.locationData,
          purchaseYear: data.purchaseYear,
        },
      });
      setSuccessUuid(result.uuid);
      setSuccessName(data.name);
    } catch (error) {
      // Error is handled by the mutation and displayed below
      console.error("Registration failed:", error);
    }
  };

  if (successUuid && successName) {
    return (
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <div className="flex flex-col gap-3">
              <div>
                <Button variant="outline" onClick={() => navigate("/")}>
                  Back
                </Button>
              </div>
              <CardTitle className="text-2xl text-center">
                ขอบคุณ {successName}! / Thank you {successName}!
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-center text-sm text-muted-foreground">
              Your registration is complete. Save this screenshot or look up your QR code again via
              your phone number.
            </p>
            <div className="flex flex-col items-center gap-3">
              <QRCode uuid={successUuid} size={192} />
              <div className="text-sm text-muted-foreground">UUID: {successUuid}</div>
              <a className="text-sm underline" href="/lookup">
                Look up via phone number
              </a>
              <div className="flex flex-col gap-2 w-full mt-2">
                <Button onClick={() => navigate(`/edit/${successUuid}`)}>
                  View & Edit Registration / ดูและแก้ไขข้อมูล
                </Button>
                <Button
                  variant="outline"
                  className="h-auto text-wrap"
                  onClick={() => navigate(`/edit/${successUuid}?mode=coupon`)}
                >
                  Register Purchase for Coupon / ลงทะเบียนซื้อเพื่อรับคูปอง
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex flex-col gap-3">
            <div>
              <Button variant="outline" onClick={() => navigate("/")}>
                Back
              </Button>
            </div>
            <CardTitle className="text-2xl text-center">
              ลงทะเบียนงาน 48th House & Condo Expo Bangkok
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Expo48Form mode="register" requireCouponFields={false} onSubmit={onSubmit}>
            {createRegistration.error && (
              <div className="text-sm text-destructive space-y-1">
                {(() => {
                  const error = createRegistration.error as Error & {
                    code?: string;
                    field?: string;
                  };
                  if (error.code === "PHONE_EXISTS") {
                    return (
                      <p>
                        เบอร์โทรศัพท์นี้ลงทะเบียนแล้ว / This phone number is already registered.{" "}
                        <a href="/lookup" className="underline">
                          ค้นหาข้อมูลของคุณ / Look up your registration
                        </a>
                      </p>
                    );
                  }
                  if (error.code === "EMAIL_EXISTS") {
                    return (
                      <p>
                        อีเมลนี้ลงทะเบียนแล้ว / This email is already registered.{" "}
                        <a href="/lookup" className="underline">
                          ค้นหาข้อมูลของคุณ / Look up your registration
                        </a>
                      </p>
                    );
                  }
                  return (
                    <p>
                      การส่งข้อมูลล้มเหลว กรุณาลองใหม่อีกครั้ง / Submission failed. Please try
                      again.
                    </p>
                  );
                })()}
              </div>
            )}
            <Button type="submit" disabled={createRegistration.isPending} className="w-full">
              {createRegistration.isPending ? "Submitting..." : "Register ลงทะเบียน"}
            </Button>
          </Expo48Form>
        </CardContent>
      </Card>
    </div>
  );
}
